/**
 * Content marketplace admin hooks — catalog + tenant orders.
 *
 * Direct fetch with credentials. The catalog rarely changes so we use a
 * long staleTime; orders should refetch on tab focus.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from './env';

async function fx(path: string, init: RequestInit = {}) {
  return fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

export const CONTENT_PACKAGES_KEY = ['admin', 'content', 'packages'] as const;
export const CONTENT_ORDERS_KEY = ['admin', 'content', 'orders'] as const;

export type ContentKind =
  | 'blog'
  | 'local_seo_city'
  | 'expert_quote'
  | 'backlink_outreach';

export interface ContentPackage {
  id: string;
  name: string;
  tagline: string;
  description: string;
  kind: ContentKind;
  cadence_per_month: number;
  price_pln_gr: number;
  price_label: string;
  scope: string[];
  sample_md: string;
  requires_tier: 'start' | 'pro' | 'pro_bundle' | 'multi';
}

export type OrderState = 'requested' | 'active' | 'paused' | 'cancelled' | 'completed';
export type OrderStage =
  | 'pending'
  | 'in_progress'
  | 'partial'
  | 'delivered'
  | 'indexed'
  | 'failed';

export interface DeliveredUrl {
  url: string;
  dr?: number;
  anchor?: string;
  indexed_at?: number;
  status?: string;
}

export interface OrderMetadata {
  landing_page_url?: string;
  anchor_texts?: string[];
  target_keywords?: string[];
  [k: string]: unknown;
}

export interface ContentOrder {
  id: string;
  tenant_id: string;
  package_id: string;
  package_name: string;
  package_kind: ContentKind | null;
  price_label: string | null;
  state: OrderState;
  stage: OrderStage;
  source: 'self_service' | 'operator' | 'stripe';
  cadence_per_month: number;
  notes: string | null;
  metadata: OrderMetadata | null;
  delivered_urls: DeliveredUrl[];
  requested_at: number;
  started_at: number | null;
  paused_at: number | null;
  cancelled_at: number | null;
  next_due_at: number | null;
  created_at: number;
  updated_at: number;
}

export function useContentPackages() {
  return useQuery({
    queryKey: CONTENT_PACKAGES_KEY,
    queryFn: async (): Promise<ContentPackage[]> => {
      const r = await fx('/api/admin/content/packages');
      if (!r.ok) throw new Error('packages_failed');
      const j = (await r.json()) as { packages: ContentPackage[] };
      return j.packages;
    },
    staleTime: 300_000,
  });
}

export function useContentOrders() {
  return useQuery({
    queryKey: CONTENT_ORDERS_KEY,
    queryFn: async (): Promise<ContentOrder[]> => {
      const r = await fx('/api/admin/content/orders');
      if (!r.ok) throw new Error('orders_failed');
      const j = (await r.json()) as { orders: ContentOrder[] };
      return j.orders;
    },
    refetchOnWindowFocus: true,
  });
}

export function useRequestOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      package_id: string;
      notes?: string;
      metadata?: OrderMetadata;
    }) => {
      const r = await fx('/api/admin/content/orders', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'order_failed');
      }
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CONTENT_ORDERS_KEY }),
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await fx(`/api/admin/content/orders/${id}/cancel`, { method: 'POST' });
      if (!r.ok) throw new Error('cancel_failed');
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CONTENT_ORDERS_KEY }),
  });
}
